// /w/:wid/mail — the inbox (Classic Frame, Oct 10 2026): the title bar with the quill (new letter), ALL / UNREAD / SENT,
// a search box, conversations grouped This week / Earlier in full-width rows. Tap a row for the thread.
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { WorldPage, useWorldCity } from "@/app/WorldPage";
import { art } from "@/kit/art";
import { ScreenHero } from "@/kit/city";
import { IconChevronRight, IconClose, IconQuill, IconSearch, IconServer } from "@/kit/icons";
import { Button, Card, CardHead, Empty, Medallion, Row, RowText, Seg, Skeleton, cx } from "@/kit/primitives";
import { useNow } from "@/state/clock";
import { useSession } from "@/state/session";
import { usePrompts } from "@/state/prompts";
import { socialKeys, useMail, useMailMutations } from "@/state/socialQueries";
import { plural } from "@/game/format";
import { fmtClock, fmtDate } from "@/game/time";
import { mailItems, mailStats, matchesSearch, matchesTab, weekGroups, type MailItem, type MailTab } from "@/game/mail";
import "../social.css";

const TABS: { value: MailTab; label: string }[] = [{ value: "all", label: "All" }, { value: "unread", label: "Unread" }, { value: "sent", label: "Sent" }];

export function MailInbox() {
  return (
    <WorldPage>
      <Inbox />
    </WorldPage>
  );
}

export function Inbox() {
  const { wid, base } = useWorldCity();
  const { id: openId } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const session = useSession();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.some((t) => t.value === params.get("t")) ? params.get("t") : "all") as MailTab;
  const setTab = (t: MailTab) => setParams(t === "all" ? {} : { t }, { replace: true });
  const [search, setSearch] = useState("");
  const q = useMail(wid);
  const { markRead } = useMailMutations(wid);
  const now = useNow(30_000);
  const [marking, setMarking] = useState(false);

  const all = useMemo(() => mailItems(q.data, session.uid), [q.data, session.uid]);
  const items = useMemo(() => all.filter((m) => matchesTab(m, tab) && matchesSearch(m, search)), [all, tab, search]);
  const groups = useMemo(() => weekGroups(items, now), [items, now]);
  const stats = useMemo(() => mailStats(all), [all]);

  // reading the list clears village/get's new_mail flag
  useEffect(() => {
    if (q.data) qc.setQueriesData({ queryKey: ["village", wid] }, (old: unknown) => (old && typeof old === "object" && "new_mail" in (old as object) ? { ...(old as object), new_mail: 0 } : old));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.dataUpdatedAt]);

  const open = (m: MailItem) => nav(`${base}/mail/${m.id}${tab === "all" ? "" : `?t=${tab}`}`);
  /** there is no mark-read call — viewing a conversation marks it; "mark all read" views each unread one */
  const markAll = async (list: MailItem[]) => {
    const unread = list.filter((m) => m.unread);
    if (!unread.length) return;
    setMarking(true);
    try {
      for (const m of unread) {
        const t = await api.mail.view({ wid }, m.id);
        qc.setQueryData(socialKeys.thread(wid, m.id), t);
        markRead(m.id);
      }
    } catch (e) {
      usePrompts.getState().error(e);
    } finally {
      setMarking(false);
    }
  };

  return (
    <div className="stack classic">
      <ScreenHero title="Mail" onBack={() => nav(base)} onGear={() => nav(`${base}/mail/new`)} gearLabel="New letter" gearIcon={<IconQuill />} />
      <Seg value={tab} onChange={setTab} options={TABS.map((t) => ({ value: t.value, label: t.label, badge: t.value === "unread" ? stats.unread || undefined : undefined }))} />
      <label className="ml-search">
        <IconSearch />
        <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search letters or players" aria-label="Search letters or players" autoComplete="off" />
        {search && <button type="button" aria-label="Clear search" onClick={() => setSearch("")}><IconClose /></button>}
      </label>

      {q.isLoading && !q.data && <div className="stack"><Skeleton h={260} /><Skeleton h={200} /></div>}
      {q.isError && !q.data && <Card><Empty>Couldn't read your mail. <Button size="sm" onClick={() => void q.refetch()}>Try again</Button></Empty></Card>}
      {q.data && !items.length && (
        <Card>
          <Empty>
            {search ? `Nothing matches "${search}".` : tab === "unread" ? "Every letter is read." : tab === "sent" ? "You haven't written anyone yet." : "No letters yet. Write to another player with the quill above."}
          </Empty>
        </Card>
      )}

      {groups.map((g) => {
        const unread = g.items.filter((m) => m.unread).length;
        return (
          <Card key={g.label}>
            <CardHead
              title={g.label}
              aside={unread ? (
                <span>{plural(unread, "unread", "unread")} · <button type="button" className="link" style={{ font: "inherit" }} disabled={marking} onClick={() => void markAll(g.items)}>{marking ? "Marking…" : "Mark all read"}</button></span>
              ) : g.aside ?? plural(g.items.length, "letter")}
            />
            <div className="rows">
              {g.items.map((m) => <MailRow key={m.id} m={m} now={now} open={m.id === openId} onOpen={() => open(m)} />)}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

export function MailRow({ m, now, open, onOpen }: { m: MailItem; now: number; /** the conversation open beside the list (split panes) */ open?: boolean; onOpen: () => void }) {
  const today = Number.isFinite(m.time) && Math.floor(m.time / 86400_000) === Math.floor(now / 86400_000);
  const when = !Number.isFinite(m.time) ? "—" : today ? `Today · ${fmtClock(m.time)}` : fmtDate(m.time);
  return (
    <Row as="button" unread={m.unread} className={cx("ml-row", m.unread && "is-unread", open && "open")} aria-current={open || undefined} onClick={onOpen} aria-label={`${m.sent ? "To" : "From"} ${m.other}: ${m.subject}${m.unread ? ", unread" : ""}`}>
      {m.system ? <Medallion size={64} className="rp-barb"><IconServer /></Medallion> : m.sent ? <Medallion src={art.avatar} alt="" size={64} /> : <Medallion initial={m.other} size={64} />}
      <RowText
        line={<span className="ml-who">{m.sent && <em>To</em>}<span>{m.other}</span></span>}
        meta={<span className="ml-subj">{m.subject}</span>}
      />
      <div className="right">
        <div className="rp-time">{m.unread && <i className="rp-dot" aria-hidden="true" />}{when}</div>
        <IconChevronRight />
      </div>
    </Row>
  );
}

